<template>
  <!-- Flash Pay in the app (lib/view/flash_pay/, dark theme): the QR screen ("Turn the phone to
       your customer", "Scan to pay", the scan help line, "Waiting for payment", "Text or email
       the link instead"), then the paid sheet ("Payment received", Customer, "New client saved",
       View invoice / New charge). Strings are app_en.arb flashPay*, except two marketing edits
       (WEB-PAGES-FACTS-2026-11-04): the scan line names the wallet condition (Apple Pay is not yet
       verified on a real iPhone), and the receipt line and Tip row are left out (no receipt send
       is proven live; tips are web-only). The amount and customer are sample data, and the code is a
       drawing, not a working QR code. Once in view the code draws in, waits, then the paid sheet
       rises (about 4 s, once). Resting styles are the final frame. -->
  <div ref="rootEl" class="sfp" :class="{ 'is-run': running || armed, 'is-armed': armed }">
    <div class="sfp-phone">
      <div class="sfp-qr-screen">
        <p class="sfp-top">Turn the phone to your customer</p>
        <strong class="sfp-amount" translate="no">$180.00</strong>
        <p class="sfp-label">Scan to pay</p>
        <svg class="sfp-qr" viewBox="0 0 25 25" shape-rendering="crispEdges">
          <rect x="0" y="0" width="25" height="25" rx="1.2" fill="#fff" />
          <rect v-for="(cell, i) in cells" :key="i" :x="cell[0] + 2" :y="cell[1] + 2" width="1" height="1" fill="#111" :style="{ '--q': `${(cell[2] * 0.9).toFixed(2)}s` }" />
        </svg>
        <p class="sfp-help">Open your camera and point it here. Card, or Apple Pay and Google Pay where supported.</p>
        <p class="sfp-wait"><i class="sfp-dot" />Waiting for payment</p>
        <p class="sfp-alt">Text or email the link instead</p>
      </div>
      <div class="sfp-paid">
        <span class="sfp-mark"><svg viewBox="0 0 24 24"><path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg></span>
        <b>Payment received</b>
        <strong class="sfp-amount sfp-amount--paid" translate="no">$180.00</strong>
        <dl class="sfp-rows">
          <div><dt>Customer</dt><dd translate="no">Sam Ortiz</dd></div>
          <div><dt>New client saved</dt><dd><svg viewBox="0 0 24 24"><path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg></dd></div>
        </dl>
        <div class="sfp-actions"><span>View invoice</span><span class="is-main">New charge</span></div>
      </div>
    </div>
  </div>
</template>

<script setup>
const rootEl = ref(null);
const { running, armed } = useMotionInView(rootEl, { threshold: 0.4 });

// A fixed 21 × 21 pattern with the three finder squares, so it reads as a QR code. Each dark
// cell carries a 0–1 delay so the code draws in from the top left.
const finder = (x, y) => {
  for (const [fx, fy] of [[0, 0], [14, 0], [0, 14]]) {
    const dx = x - fx;
    const dy = y - fy;
    if (dx >= 0 && dx < 7 && dy >= 0 && dy < 7) {
      const ring = Math.max(Math.abs(dx - 3), Math.abs(dy - 3));
      return ring === 3 || ring <= 1 ? 1 : 0;
    }
    if (dx >= -1 && dx <= 7 && dy >= -1 && dy <= 7) return 0;
  }
  return -1;
};
const cells = [];
let seed = 369;
const rand = () => {
  seed = (seed * 1103515245 + 12345) % 2147483648;
  return seed / 2147483648;
};
for (let y = 0; y < 21; y++) {
  for (let x = 0; x < 21; x++) {
    const f = finder(x, y);
    const on = f === -1 ? rand() > 0.52 : f === 1;
    if (on) cells.push([x, y, (x + y) / 40]);
  }
}
</script>

<style scoped>
.sfp { display: grid; place-items: center; }
.sfp-phone {
  position: relative;
  width: 270px;
  height: 470px;
  overflow: hidden;
  border: 1px solid rgb(255 255 255 / 0.16);
  border-radius: 22px;
  background: #1c1c1c;
  color: #ececec;
  font-family: "Space Grotesk", Inter, ui-sans-serif, system-ui, sans-serif;
  text-align: center;
  box-shadow: 0 24px 60px -20px rgb(0 0 0 / 0.8);
}
.sfp-qr-screen, .sfp-paid { position: absolute; inset: 0; display: grid; align-content: start; justify-items: center; padding: 26px 20px 20px; }
.sfp-top { color: #a8a8a8; font-size: 11.5px; }
.sfp-amount { margin-top: 6px; font-size: 30px; font-weight: 700; line-height: 1.1; font-variant-numeric: tabular-nums; }
.sfp-label { margin-top: 4px; font-size: 13px; font-weight: 600; }
.sfp-qr { width: 168px; height: 168px; margin-top: 14px; border-radius: 6px; }
.sfp-help { max-width: 210px; margin-top: 12px; color: #a8a8a8; font-size: 11.5px; line-height: 1.4; }
.sfp-wait { display: flex; align-items: center; gap: 7px; margin-top: 14px; font-size: 12.5px; font-weight: 600; }
.sfp-dot { width: 8px; height: 8px; border-radius: 2px; background: #f1c95e; }
.sfp-alt { margin-top: 12px; color: #93aef9; font-size: 12px; }

.sfp-paid { gap: 2px; background: #1c1c1c; }
.sfp-mark { display: grid; place-items: center; width: 46px; height: 46px; margin: 18px 0 8px; border-radius: 50%; background: rgb(63 178 110 / 0.18); color: #6ed39a; }
.sfp-mark svg { width: 24px; height: 24px; fill: currentColor; }
.sfp-paid > b { font-size: 15px; font-weight: 600; }
.sfp-amount--paid { margin-top: 2px; }
.sfp-rows { width: 100%; margin: 16px 0 0; border: 1px solid rgb(255 255 255 / 0.12); border-radius: 8px; background: #262626; text-align: left; }
.sfp-rows div { display: flex; justify-content: space-between; align-items: center; padding: 8px 12px; font-size: 12.5px; }
.sfp-rows div + div { border-top: 1px solid rgb(255 255 255 / 0.1); }
.sfp-rows dt { color: #a8a8a8; }
.sfp-rows dd { margin: 0; font-weight: 600; font-variant-numeric: tabular-nums; }
.sfp-rows svg { width: 15px; height: 15px; fill: #6ed39a; }
.sfp-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; width: 100%; margin-top: 18px; font-size: 12.5px; font-weight: 600; }
.sfp-actions span { border: 1px solid rgb(255 255 255 / 0.16); border-radius: 7px; padding: 9px 0; }
.sfp-actions .is-main { border-color: transparent; background: #ececec; color: #171717; }

/* Resting frame: paid. When it plays: the code draws in, the wait dot pulses, then the sheet rises. */
.is-run .sfp-qr rect:not(:first-child) { animation: sfp-cell 0.25s ease-out calc(0.2s + var(--q)) both; }
.is-run .sfp-dot { animation: sfp-blink 0.8s ease-in-out 1.3s 3 both; }
.is-run .sfp-paid { animation: sfp-rise 0.55s cubic-bezier(0.33, 1, 0.68, 1) 3.6s both; }
.is-run .sfp-paid > * { animation: sfp-fade 0.4s ease-out calc(3.8s + var(--i, 0) * 0.08s) both; }
.sfp-paid > :nth-child(2) { --i: 1; }
.sfp-paid > :nth-child(3) { --i: 2; }
.sfp-paid > :nth-child(4) { --i: 3; }
.sfp-paid > :nth-child(5) { --i: 4; }
.sfp-paid > :nth-child(6) { --i: 5; }
.is-armed * { animation-play-state: paused !important; }
@keyframes sfp-cell { from { opacity: 0; } to { opacity: 1; } }
@keyframes sfp-blink { 50% { opacity: 0.25; } }
@keyframes sfp-rise { from { transform: translateY(100%); } to { transform: none; } }
@keyframes sfp-fade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .sfp * { animation: none !important; } }
</style>
